import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase, getSupabaseUrl } from '../utils/supabase';
import RenderMonitor from '../components/RenderMonitor';
import { getCache, setCache } from '../utils/cache';

function TeamEditMembers() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [team, setTeam] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [removingUser, setRemovingUser] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [addEmails, setAddEmails] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addResults, setAddResults] = useState([]);

  useEffect(() => {
    const TEAM_INFO_KEY = `team.${id}.info.v1`;
    const TEAM_USERS_KEY = `team.${id}.users.v1`;

    const passedTeamName = location?.state?.teamName || location?.state?.team?.name || null;
    const passedClientId = location?.state?.clientId || location?.state?.team?.client_id || null;
    const passedRegionId = location?.state?.team?.region_id || null;

    const cachedTeam = getCache(TEAM_INFO_KEY, 5 * 60 * 1000);
    const cachedUsers = getCache(TEAM_USERS_KEY, 2 * 60 * 1000);

    // Seed UI from state/cache
    if (passedTeamName || cachedTeam) {
      setTeam(cachedTeam || { id: Number(id), name: passedTeamName, client_id: passedClientId, region_id: passedRegionId });
    }
    if (Array.isArray(cachedUsers)) {
      setUsers(cachedUsers);
      setLoading(false);
    } else {
      setLoading(true);
    }

    const run = async () => {
      setError('');
      // Fetch team info only if not provided and not cached
      if (!passedTeamName && !cachedTeam) {
        console.log('[TEAM_EDIT][DB] SELECT team info', { teamId: id });
        const { data: teamData, error: teamError } = await supabase
          .from('teams')
          .select('id, name, client_id, region_id')
          .eq('id', id)
          .single();
        if (!teamError && teamData) {
          setTeam(teamData);
          setCache(TEAM_INFO_KEY, teamData);
        } else if (teamError) {
          setError('Could not fetch team');
        }
      }

      // Always revalidate team users in background
      console.log('[TEAM_EDIT][DB] SELECT team users', { teamId: id });
      const { data: teamUsersData, error: teamUsersError } = await supabase
        .from('team_users')
        .select('user_id, users(full_name, email)')
        .eq('team_id', id);
      if (teamUsersError) {
        setError('Could not fetch team users');
      } else {
        const usersList = (teamUsersData || []).map(tu => {
          const u = tu.users;
          return {
            id: tu.user_id,
            full_name: u?.full_name,
            email: u?.email,
          };
        });
        console.log('[TEAM_EDIT][DB][RESULT] users', { count: usersList.length });
        setUsers(usersList);
        setCache(TEAM_USERS_KEY, usersList);
      }

      setLoading(false);
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleRemoveUser = async (userId) => {
    setRemoving(true);
    console.log('[TEAM_EDIT][DB] DELETE team_user', { teamId: id, userId });
    await supabase
      .from('team_users')
      .delete()
      .eq('team_id', id)
      .eq('user_id', userId);
    const updated = users.filter(u => u.id !== userId);
    setUsers(updated);
    setCache(`team.${id}.users.v1`, updated);
    
    // Invalidate region cache for this team's region to ensure fresh data on next load
    try {
      let clientId = team?.client_id || location?.state?.clientId;
      let regionId = team?.region_id;
      if (!clientId || !regionId) {
        const { data: tinfo } = await supabase
          .from('teams')
          .select('client_id, region_id')
          .eq('id', id)
          .single();
        if (tinfo) {
          clientId = clientId || tinfo.client_id;
          regionId = regionId || tinfo.region_id;
        }
      }
      if (clientId && regionId) {
        const regionCacheKey = `client.${clientId}.region.${regionId}.teams.v1`;
        localStorage.removeItem(regionCacheKey);
      }
    } catch {}
    
    // Optimistically update client snapshot (legacy cache)
    try {
      const clientId = team?.client_id || location?.state?.clientId;
      if (clientId) {
        const SNAPSHOT_KEY = `client.${clientId}.snapshot.v2`;
        const raw = localStorage.getItem(SNAPSHOT_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          const data = parsed?.data || parsed;
          const currentTeams = Array.isArray(data?.teams) ? data.teams : [];
          const updatedTeams = currentTeams.map(t => (t.id === Number(id) ? { ...t, users: updated } : t));
          // Update regions block if present
          let updatedRegions = Array.isArray(data?.regions) ? data.regions.map(r => ({
            ...r,
            teams: Array.isArray(r.teams) ? r.teams.map(rt => (rt.id === Number(id) ? { ...rt, users: updated } : rt)) : r.teams
          })) : data?.regions;
          const next = { ...data, teams: updatedTeams, regions: updatedRegions, userCount: 0 };
          localStorage.setItem(SNAPSHOT_KEY, JSON.stringify({ savedAt: Date.now(), data: next }));
        }
      }
    } catch {}
    setRemovingUser(null);
    setRemoving(false);
  };

  const handleAddMembers = async () => {
    setAddLoading(true);
    setAddResults([]);
    const emails = addEmails
      .split(/[,\s]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const jwt = sessionData?.session?.access_token;
      const baseUrl = getSupabaseUrl();
      console.log('[TEAM_EDIT][EDGE] POST admin-add-users-to-team', { teamId: id, emails: emails.length });
      const resp = await fetch(`${baseUrl}/functions/v1/admin-add-users-to-team`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwt}`
        },
        body: JSON.stringify({ emails, team_id: Number(id), client_id: team?.client_id })
      });
      let payload = null;
      try {
        payload = await resp.json();
      } catch (_) {
        // ignore
      }
      if (!resp.ok) {
        const message = (payload && payload.error) ? payload.error : (resp.statusText || 'Request failed');
        setAddResults([{ email: '-', status: 'error', message }]);
      } else {
        setAddResults((payload && payload.results) ? payload.results : []);
      }
    } catch (e) {
      setAddResults([{ email: '-', status: 'error', message: e.message || 'Request failed' }]);
    }
    setAddLoading(false);
    setAddEmails('');
    // Refresh users list
    console.log('[TEAM_EDIT][DB] REFRESH team users');
    const { data: teamUsersData } = await supabase
      .from('team_users')
      .select('user_id, users(full_name, email)')
      .eq('team_id', id);
    const usersList = (teamUsersData || []).map(tu => {
      const u = tu.users;
      return {
        id: tu.user_id,
        full_name: u?.full_name,
        email: u?.email,
      };
    });
    setUsers(usersList);
    setCache(`team.${id}.users.v1`, usersList);
    
    // Invalidate region cache for this team's region to ensure fresh data on next load
    try {
      let clientId = team?.client_id || location?.state?.clientId;
      let regionId = team?.region_id;
      if (!clientId || !regionId) {
        const { data: tinfo } = await supabase
          .from('teams')
          .select('client_id, region_id')
          .eq('id', id)
          .single();
        if (tinfo) {
          clientId = clientId || tinfo.client_id;
          regionId = regionId || tinfo.region_id;
        }
      }
      if (clientId && regionId) {
        const regionCacheKey = `client.${clientId}.region.${regionId}.teams.v1`;
        localStorage.removeItem(regionCacheKey);
      }
    } catch {}
    
    // Optimistically update client snapshot (legacy cache)
    try {
      const clientId = team?.client_id || location?.state?.clientId;
      if (clientId) {
        const SNAPSHOT_KEY = `client.${clientId}.snapshot.v2`;
        const raw = localStorage.getItem(SNAPSHOT_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          const data = parsed?.data || parsed;
          const currentTeams = Array.isArray(data?.teams) ? data.teams : [];
          const updatedTeams = currentTeams.map(t => (t.id === Number(id) ? { ...t, users: usersList } : t));
          let updatedRegions = Array.isArray(data?.regions) ? data.regions.map(r => ({
            ...r,
            teams: Array.isArray(r.teams) ? r.teams.map(rt => (rt.id === Number(id) ? { ...rt, users: usersList } : rt)) : r.teams
          })) : data?.regions;
          const next = { ...data, teams: updatedTeams, regions: updatedRegions, userCount: 0 };
          localStorage.setItem(SNAPSHOT_KEY, JSON.stringify({ savedAt: Date.now(), data: next }));
        }
      }
    } catch {}
  };

  return (
    <div className="modern-container-narrow">
      <RenderMonitor componentName="TeamEditMembers" />
      
      <button
        type="button"
        onClick={() => {
          const cid = team?.client_id || location?.state?.clientId;
          const rid = team?.region_id || location?.state?.team?.region_id;
          if (cid && rid) {
            navigate(`/client/${cid}/region/${rid}`, { replace: true, state: { refresh: Date.now() } });
            return;
          }
          if (cid) {
            navigate(`/client/${cid}`, { replace: true, state: { refresh: Date.now() } });
          }
        }}
        className="modern-button modern-button-secondary modern-mb-6"
      >
        ← Back
      </button>
      
      <div className="modern-card">
        <h1 className="modern-title">{team?.name ? `${team.name}` : 'Team'} – Edit Members</h1>
        
        {loading ? (
          <div className="modern-loading">Loading team members...</div>
        ) : error ? (
          <div className="modern-error">{error}</div>
        ) : (
          <div className="modern-flex modern-flex-wrap modern-gap-3">
            {users.length === 0 ? (
              <span style={{ color: '#94a3b8' }}>No users in this team.</span>
            ) : (
              users.map(user => (
                <div
                  key={user.id}
                  className="modern-user-tag modern-user-tag-removable"
                  style={{ cursor: 'pointer' }}
                  title={user.email || ''}
                  onClick={() => setRemovingUser(user)}
                >
                  <span>
                    {user.full_name ? user.full_name : user.email}
                  </span>
                  <button
                    className="modern-user-tag-remove"
                    onClick={e => { e.stopPropagation(); setRemovingUser(user); }}
                    aria-label="Remove user"
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Confirmation pop-up */}
      {removingUser && (
        <div className="modern-modal-overlay">
          <div className="modern-modal">
            <button onClick={() => setRemovingUser(null)} className="modern-modal-close">&times;</button>
            <h3 className="modern-heading">Remove User</h3>
            <p>Are you sure you want to remove <b>{removingUser.full_name ? removingUser.full_name : removingUser.email}</b> from this team?</p>
            <button
              onClick={() => handleRemoveUser(removingUser.id)}
              disabled={removing}
              className="modern-button modern-button-danger"
              style={{ width: '100%', marginTop: '16px' }}
            >
              {removing ? 'Removing...' : 'Confirm Remove'}
            </button>
          </div>
        </div>
      )}

      {/* Add Users Section */}
      <div className="modern-card">
        <h2 className="modern-heading">Add Users</h2>
        <textarea
          value={addEmails}
          onChange={e => setAddEmails(e.target.value)}
          placeholder="Enter emails separated by commas, spaces, or newlines"
          className="modern-textarea"
          style={{ 
            width: '100%', 
            minHeight: '120px', 
            resize: 'vertical',
            marginBottom: '16px'
          }}
          disabled={addLoading}
        />
        <button
          onClick={handleAddMembers}
          disabled={addLoading || !addEmails.trim()}
          className="modern-button modern-button-success"
          style={{ width: '100%', marginBottom: '16px' }}
        >
          {addLoading ? 'Adding...' : 'Add Members'}
        </button>
        {addResults.length > 0 && (
          <div className="modern-mt-4">
            {addResults.map((res, i) => (
              <div key={i} style={{ 
                color: res.status === 'added' ? '#166534' : res.status === 'exists' ? '#92400e' : '#991b1b',
                marginBottom: '4px'
              }}>
                {res.email}: {res.message}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Single back button is provided at the top; removing duplicate at bottom */}
    </div>
  );
}

export default TeamEditMembers; 